<script setup lang="ts">
  import CompleteTime from '@/components/CompleteTime.vue';
  import { useClipboard } from '@/composition/use-clipboard';
  import { useAuth } from '@/composition/use-auth';
  import { useOrders } from '@/composition/use-orders';
  import { ORDER_TYPES_MAP } from '@/const/orders.const';
  import { PAYMENT_METHODS_MAP } from '@/const/payment.consts';
  import { OrderType } from '@/enums/OrderEnums';
  import { PermissionType } from '@/enums/PermissionType';
  import { IOrder } from '@/interfaces/IOrder';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { bitCount, displayPrice } from '@/utils';
  import { stringDateToHHMM } from '@/utils/date.utils';
  import { orderItemsToText } from '@/utils/orders.utils';
  import { computed, inject, Ref } from 'vue';

  const order = inject<Ref<IOrder>>('order')!;

  const restaurantStore = useRestaurantStore();
  const orderHelper = useOrders();
  const clipboard = useClipboard();
  const auth = useAuth();

  // Editing requires OrdersManage (the edit flow routes into the POS): an
  // OrdersHistoric-only back-office user must not see the entry point.
  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  const orderType = computed(() => ORDER_TYPES_MAP.get(order.value.type));

  /*

    get multiplePaymentMethods() {
      return bitCount(this.activePaymentMethods) > 1;
    }

    get activePaymentMethods() {
      switch (this.order.type) {
        case OrderType.Seat:
          return this.branch.paymentMethodsA;
        case OrderType.Away:
          return this.branch.paymentMethodsB;
        case OrderType.Delivery:
          return this.branch.paymentMethodsC;
      }
    }*/

  const activePaymentMethods = computed(() => {
    switch (order.value.type) {
      case OrderType.Seat:
        return restaurantStore.selectedBranch?.paymentMethodsA;
      case OrderType.Away:
        return restaurantStore.selectedBranch?.paymentMethodsB;
      case OrderType.Delivery:
        return restaurantStore.selectedBranch?.paymentMethodsC;
    }
  });

  const multiplePaymentMethods = computed(() => {
    return bitCount(activePaymentMethods.value ?? 0) > 1;
  });

  const showPaymentMethod = computed(() => {
    return (
      multiplePaymentMethods.value &&
      !order.value.manual &&
      order.value.paymentMethod
    );
  });

  // Time (HH:mm)
  const displayTime = computed(() => {
    const date = new Date(order.value.createdAt);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  });

  const orderTel = computed(() => {
    return order.value.userTel || order.value.userWhatsapp;
  });

  const orderAddress = computed(() => {
    if (order.value.type !== OrderType.Delivery) return null;

    let address = order.value.userAddress;

    if (order.value.userAddress2) {
      address += ', ' + order.value.userAddress2;
    }

    return address;
  });

  function editOrder(startingMode?: 'edit' | 'view') {
    orderHelper.editOrder(order.value, startingMode);
  }

  const telLinks = computed(() => {
    const tel = orderTel.value;
    if (!tel) return null;

    const wappMessage = `Hola! Me comunico por su pedido en ${restaurantStore.restaurant?.name}.`;

    return {
      whatsapp: `https://wa.me/${tel
        .replace('+', '')
        .replace('-', '')}?text=${encodeURIComponent(wappMessage)}`,
      phone: `tel:${tel}`,
    };
  });

  const posText = computed(() => {
    const o = order.value;

    if (!o.manual) {
      return 'menú digital';
    }

    if (o.deviceId === restaurantStore.deviceId) {
      return 'este POS';
    }

    return 'un POS';
  });

  const mapLink = computed(() => {
    if (order.value.type !== OrderType.Delivery || !order.value.deliveryLat)
      return null;

    return `https://www.google.com/maps/search/?api=1&query=${order.value.deliveryLat},${order.value.deliveryLng}`;
  });
</script>

<template>
  <div class="order-info-container">
    <div class="header">
      <div class="header-main">
        <v-chip class="order-type" small>
          <v-icon class="pr-1" small>{{ orderType.icon }}</v-icon>
          {{ orderType.title }}
        </v-chip>
        <v-btn
          class="order-id-btn"
          x-small
          text
          color="grey"
          @click="clipboard.copyText(order.id + '')"
        >
          #{{ order.id }}
          <v-icon x-small right class="icon">mdi-content-copy</v-icon>
        </v-btn>
      </div>
      <v-btn
        v-if="canManageOrders"
        x-small
        color="blue"
        dark
        depressed
        rounded
        @click="editOrder('edit')"
      >
        <v-icon left small>mdi-pencil</v-icon>
        Editar
      </v-btn>
    </div>
    <div class="body">
      <template v-if="order.type === OrderType.Seat">
        <div class="info-line">
          <v-icon small>mdi-table-furniture</v-icon>
          <span>Mesa {{ order.table?.name }}</span>
        </div>
        <div
          v-if="order.covers != null || order.coversSuggested != null"
          class="info-line"
        >
          <v-icon small>mdi-account-group</v-icon>
          <span class="line-key">Cubiertos</span>
          <span v-if="order.covers != null">{{ order.covers }}</span>
          <span v-else>{{ order.coversSuggested }} (sugerido)</span>
        </div>
      </template>
      <template v-else-if="order.type === OrderType.Delivery">
        <div class="info-line">
          <v-icon small>mdi-map-marker</v-icon>
          <span>{{ orderAddress }}</span>
          <v-btn
            v-if="mapLink"
            class="ml-2"
            x-small
            icon
            text
            color="success"
            target="_blank"
            :href="mapLink"
          >
            <v-icon>mdi-map-search</v-icon>
          </v-btn>
        </div>
        <div v-if="order.userAddressComments" class="info-line">
          <v-icon small>mdi-map-marker-alert-outline</v-icon>
          <span>{{ order.userAddressComments }}</span>
        </div>
      </template>
      <template v-else-if="order.type === OrderType.Away">
        <div class="info-line">
          <!-- Order id -->
          <v-icon small>mdi-receipt</v-icon>
          <span># {{ order.humanId }}</span>
        </div>
      </template>
      <div class="info-line">
        <v-icon v-if="order.userName" small>mdi-account</v-icon>
        <span>{{ order.userName }}</span>
      </div>
      <div v-if="order.clubMember" class="info-line">
        <v-icon small>mdi-star-circle</v-icon>
        <span class="line-key">{{ $t('posV2.metaClub') }}</span>
        <span>
          {{ order.clubMember.name }}
          ({{
            $t('posV2.clubMemberNumber', {
              number: order.clubMember.memberNumber,
            })
          }})
        </span>
      </div>
      <div v-if="showPaymentMethod" class="info-line">
        <v-icon small>mdi-credit-card</v-icon>
        <span>{{ PAYMENT_METHODS_MAP.get(order.paymentMethod)?.title }}</span>
      </div>
      <template v-if="order.orderFields?.length">
        <div
          v-for="orderField in order.orderFields"
          v-if="orderField.value"
          :key="orderField.fieldId"
          class="info-line"
        >
          <v-icon small>mdi-text</v-icon>
          <span class="line-key">{{ orderField.field?.title }}</span>
          <span>{{ orderField.value }}</span>
        </div>
      </template>
      <div v-if="orderTel" class="info-line">
        <v-icon small>mdi-phone</v-icon>
        <span>{{ orderTel }}</span>
        <v-btn
          class="ml-2"
          x-small
          icon
          text
          color="success"
          target="_blank"
          :href="telLinks.whatsapp"
        >
          <v-icon>mdi-whatsapp</v-icon>
        </v-btn>
        <v-btn
          class="ml-2"
          x-small
          icon
          text
          color="success"
          target="_blank"
          :href="telLinks.phone"
        >
          <v-icon>mdi-phone</v-icon>
        </v-btn>
      </div>
      <div v-if="order.comment" class="info-line">
        <v-icon small>mdi-comment</v-icon>
        <span>{{ order.comment }}</span>
      </div>
    </div>
    <div class="footer">
      <CompleteTime :date="order.createdAt" class="time" />
      <span class="pos-text"
        >desde <strong>{{ posText }}</strong></span
      >
      <span v-if="order.user" class="pos-text"
        >por <strong>{{ order.user.name }}</strong></span
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .order-info-container {
    @apply tw-rounded-xl tw-py-3 tw-bg-white tw-border tw-border-gray-300 tw-border-dashed;

    .header {
      @apply tw-flex tw-items-center tw-justify-between tw-space-x-2 tw-pb-1.5 tw-px-3;

      .header-main {
        @apply tw-flex tw-items-center;

        .order-id-btn {
          @apply tw-px-1;

          .icon {
            @apply tw-ml-0;
          }
        }
      }

      .order-total {
        @apply tw-font-semibold tw-text-black;
      }
    }

    .body {
      @apply tw-space-y-1.5 tw-py-0.5 tw-px-3;

      .info-line {
        @apply tw-flex tw-items-center tw-space-x-1 tw-text-black tw-text-sm;
        line-height: 1.2em;

        .line-key {
          @apply tw-font-medium tw-text-gray-500;
        }
      }
    }

    .footer {
      @apply tw-space-x-1 tw-pt-1 tw-px-3;

      line-height: 1em;

      & > * {
        @apply tw-inline;
      }

      .pos-text {
        @apply tw-text-xs tw-text-gray-500;
      }
    }
  }
</style>
